import * as regexes from "./MetarTafRegExp";
import * as abbr from "./MetarTafAbbreviations";
import { getMatch } from "../CommonParsers";
import {
  ICloud,
  IRwy,
  ITemp,
  IWeatherDesc,
  IWind,
  EMinMax,
  EProb,
  EPlusMinus,
  ERvrTrend,
  ICommonForecastProperties,
  ECloudTypes,
  ENA,
  ECloudAmount,
  ITafForecast,
  IValidity,
} from "@naviair-utm/node-shared-interfaces";
import { generateUTCDate } from "../Date";

/**
 * Get the date and time for the metar/taf
 */
export const dateParser = (input: string): Date | undefined => {
  const date: string = getMatch(input.match(regexes.dateRegex));
  if (date !== "") {
    const day = parseInt(date.substring(0, 2));
    const hour = parseInt(date.substring(2, 4));
    const minute = parseInt(date.substring(4, 6));
    return generateUTCDate(minute, hour, day).toDate();
  } else {
    return undefined;
  }
};

export const tafValidityParser = (
  input: string
): { start: Date; end: Date; validityPeriod: number } | undefined => {
  // Parse a ddhh/ddhh like 1608/1618
  const validityPeriod: string = getMatch(
    input.match(regexes.tafValidityRegex)
  );
  if (validityPeriod && validityPeriod !== "") {
    const dayStart = parseInt(validityPeriod.substring(0, 2));
    const hourStart = parseInt(validityPeriod.substring(2, 4));
    const hourEnd = parseInt(validityPeriod.substring(7, 9));
    // If the end hour is less than the start hour, then the end day is the next day
    const dayEnd = parseInt(validityPeriod.substring(5, 7)) + (hourEnd === 24 ? 1 : 0);
    // Generate two date objects with the start and end time
    const start = generateUTCDate(0, hourStart, dayStart);
    const end = generateUTCDate(0, hourEnd, dayEnd);
    // Get hours between start and end by getting the milliseconds from both dates, subtract them and then divide by 36e5
    const hoursBetween = end.diff(start, "hours", true);
    return {
      start: start.toDate(),
      end: end.toDate(),
      validityPeriod: hoursBetween,
    };
  } else {
    return undefined;
  }
};

/**
 * Check if the weather report is auto generated
 */
export const autoParser = (input: string): boolean => {
  return input.toUpperCase().search(" AUTO ") !== -1;
};

export const windParser = (input: string): IWind | null => {
  const wind: RegExpMatchArray | null = input.match(regexes.windRegex);
  let windObj: IWind = {};
  // The match groups has their own index in the RegExpMatchArray so we will check for the index that is defined
  if (wind !== null) {
    if (wind[4] != undefined) {
      // Matched VRB23G04KT
      windObj.dir = wind[4].substring(0, 3);
      windObj.speed = parseInt(wind[4].substring(3, 5));
      windObj.gusts = parseInt(wind[4].substring(6, 8));
    } else if (wind[3] !== undefined) {
      // Matched VRB02KT
      windObj.dir = wind[3].substring(0, 3);
      windObj.speed = parseInt(wind[3].substring(3, 5));
    } else if (wind[2] !== undefined) {
      // Matched 23004G08KT
      windObj.dir = parseInt(wind[2].substring(0, 3));
      windObj.speed = parseInt(wind[2].substring(3, 5));
      windObj.gusts = parseInt(wind[2].substring(6, 8));
    } else if (wind[1] !== undefined) {
      // Matched 23004KT
      windObj.dir = parseInt(wind[1].substring(0, 3));
      windObj.speed = parseInt(wind[1].substring(3, 5));
    } else {
      return null;
    }
  } else {
    return null;
  }

  // If the wind direction is between two values
  const windVar: string = getMatch(input.match(regexes.windVarRegex));
  if (windVar !== "") {
    // Get the two degrees and check if they are numbers which they should
    const dir1: number = !isNaN(+windVar.substring(0, 3))
      ? parseInt(windVar.substring(0, 3))
      : -1;
    const dir2: number = !isNaN(+windVar.substring(4, 7))
      ? parseInt(windVar.substring(4, 7))
      : -1;

    if (dir1 !== -1 && dir2 !== -1) {
      // Set min and max dir (numeric value)
      windObj.varDirMin = dir1 >= dir2 ? dir2 : dir1;
      windObj.varDirMax = dir1 >= dir2 ? dir1 : dir2;
    }
  }

  return windObj;
};

/**
 * Get visibility from the weather report
 */
export const visibilityParser = (
  input: string
): { visibility: string; ndv?: boolean } | null => {
  const visibility: RegExpMatchArray | null = input.match(
    regexes.visibilityRegex
  );
  if (visibility !== null) {
    const returnObj = { visibility: "", ndv: false };
    if (visibility[2]) {
      // This matches the second parentheses and is matched if METAR contains "NDV"
      returnObj.ndv = true;
    }

    visibility.forEach((val) => {
      if (val) returnObj.visibility = val;
    });

    return returnObj;
  } else {
    return null;
  }
};

/**
 * Get temperature and dew point
 */
export const tempParser = (
  input: string
): { temp: number; dewPoint: number } | null => {
  const temp: string = getMatch(input.match(regexes.tempRegex));
  if (temp !== "") {
    const returnObj = { temp: 0, dewPoint: 0 };
    returnObj.temp = parseInt(temp.substring(0, 2));
    returnObj.dewPoint = parseInt(temp.substring(3, 5));
    return returnObj;
  } else {
    return null;
  }
};

/**
 * Parse cloud groups. Formation, altitude and type of cloud
 */
export const cloudParser = (input: string): Array<ICloud> | null => {
  const clouds: RegExpMatchArray | null = input.match(regexes.cloudRegex);
  // This will return an array of strings for each cloud formation in the METAR
  if (clouds !== null) {
    const cloudsArray: Array<ICloud> = [];

    // Loop through each cloud formation and add to this array
    for (const cloud of clouds) {
      const cloudFormation = cloud.substring(0, 3);
      // If the cloud formation is not found in the object of cloud abbreviations, then something is wrong with the match
      if (cloudFormation in abbr.cloudAbbreviations) {
        const cloudObj: ICloud = {} as ICloud;

        // Get the cloud formation as either slashes or a three character string
        if (cloudFormation === "///") {
          cloudObj.cloudAmount = ENA.NA;
          cloudObj.cloudAmountDescription = "Not available";
        } else if (Object.keys(ECloudAmount).includes(cloudFormation)) {
          for (let key in ECloudAmount) {
            if (key === cloudFormation) {
              const enumKey = key as keyof typeof ECloudAmount;
              cloudObj.cloudAmount = ECloudAmount[enumKey];

              // Add the description if supported
              if (cloudFormation in abbr.cloudAbbreviations) {
                cloudObj.cloudAmountDescription =
                  abbr.cloudAbbreviations[cloudFormation];
              }
            }
          }
        }

        // Cloud altitude is three numbers or slashes
        const altitude = cloud.substring(3, 6);
        if (altitude === "///") {
          cloudObj.cloudAltitude = ENA.NA;
        } else {
          cloudObj.cloudAltitude = parseInt(altitude);
        }

        // Check if the type of cloud is provided in the string
        if (cloud.toUpperCase().search("TCU") !== -1) {
          cloudObj.type = ECloudTypes.TCU;
        } else if (cloud.toUpperCase().search("CB") !== -1) {
          cloudObj.type = ECloudTypes.CB;
        } else if (cloud.length === 9 && cloud.substring(cloud.length-3) === "///") {
          cloudObj.type = ENA.NA;
        }

        cloudsArray.push(cloudObj);
      }
    }
    return cloudsArray;
  } else {
    return null;
  }
};

/**
 * Get the weather phenomenons and intensity
 */
export const weatherParser = (input: string): Array<IWeatherDesc> | null => {
  const weatherMatch: RegExpMatchArray | null = input.match(
    regexes.weatherRegex
  );
  let weatherArray: Array<IWeatherDesc> = [];

  if (weatherMatch !== null) {
    // Loop through all weather groups
    for (let weather of weatherMatch) {
      let weatherString: string = weather.trim(); // Remove unnecessary whitespace
      let weatherObj: IWeatherDesc = { abbreviation: [], description: [] };

      // Get is there is any symbol (+ or -) to describe intensity. None is default moderate
      if (weatherString.substring(0, 1) == "+") {
        weatherObj.intensitySymbol = "+";
        weatherObj.intensityDescription = "Heavy";
      } else if (weatherString.substring(0, 1) == "-") {
        weatherObj.intensitySymbol = "-";
        weatherObj.intensityDescription = "Light";
      } else if (weatherString.substring(0, 2) === "VC") {
        weatherObj.intensitySymbol = "VC";
        weatherObj.intensityDescription = "In the vicinity";
      } else {
        weatherObj.intensitySymbol = "";
        weatherObj.intensityDescription = "Moderate";
      }

      //Get the array of weather. To character codes (see the weatherAbbreviations array)
      const weatherStrings = weatherString.match(
        regexes.weatherAbbreviationRegex
      );
      if (weatherStrings !== null) {
        for (const a of weatherStrings) {
          weatherObj.abbreviation.push(a);
          weatherObj.description.push(abbr.weatherAbbreviations[a]);
        }
      }

      weatherArray.push(weatherObj);
    }

    return weatherArray;
  } else {
    return null;
  }
};

/**
 * Check if the weather report has been corrected
 */
export const corParser = (input: string): boolean => {
  return input.toUpperCase().search(" COR ") !== -1;
};

/**
 * Get visual range of runways as an array
 */
export const rwyVisualRng = (input: string): Array<IRwy> => {
  const rwys: RegExpMatchArray | null = input.match(
    regexes.metarRwyVisualRegex
  );

  if (rwys !== null) {
    let rwysArray: Array<IRwy> = [];

    // Now run the same regex but without global so that the capture groups will separate the strings into the pieces we want
    for (let rwy of rwys) {
      const rwyParts = rwy.match(regexes.rwySeparator);

      // The groups key contains all the information separated for easy use
      if ("groups" in rwyParts) {
        // These two informations are required of the metar format if a rwy is specified
        let rwyObj: IRwy = {
          identifier: rwyParts[1],
          visualRange: parseInt(rwyParts[3]),
        };

        // Check for plus or minus
        if (rwyParts[2] !== "") {
          const plusMinus = rwyParts[2];
          if (plusMinus == "P") {
            rwyObj.plusMinus = EPlusMinus.PLUS;
          } else if (plusMinus == "M") {
            rwyObj.plusMinus = EPlusMinus.MINUS;
          }
        }

        // Check for trend (U, D or V)
        if (rwyParts[4] !== "") {
          const trend = rwyParts[4];
          if (trend === "U") {
            rwyObj.trend = ERvrTrend.U;
          } else if (trend === "D") {
            rwyObj.trend = ERvrTrend.D;
          } else if (trend === "V") {
            rwyObj.trend = ERvrTrend.V;
          }
        }

        rwysArray.push(rwyObj);
      }
    }
  }

  if (rwys !== null) {
    let rwysArray: Array<IRwy> = [];
    // Loop through the runways and get identifier and the visual range of 4 digits after the slash
    for (let rwy of rwys) {
      rwysArray.push({
        identifier: rwy.substring(1, rwy.indexOf("/")),
        visualRange: parseInt(rwy.substring(rwy.length-4)),
      });
    }

    return rwysArray;
  } else {
    return null;
  }
};

const verticalVisibilityParser = (input: string): number | ENA | null => {
  const vv: RegExpMatchArray | null = input.match(regexes.verticalVisibility);
  if (vv !== null) {
    const vvMatch = getMatch(vv);
    if (vvMatch === "///") {
      return ENA.NA;
    } else {
      return parseInt(vvMatch);
    }
  } else {
    return null;
  }
};

const changeParser = (input: string): string | undefined => {
  const changeIndicator: RegExpMatchArray | null = input.match(
    regexes.tafChangeRegex
  );
  if (changeIndicator !== null) {
    return getMatch(changeIndicator);
  } else {
    return undefined;
  }
};

const probParser = (input: string): string | undefined => {
  const prob: RegExpMatchArray | null = input.match(regexes.tafProbRegex);
  if (prob !== null) {
    return getMatch(prob);
  } else {
    return undefined;
  }
};

const tafTempParser = (input: string): Array<ITemp> | undefined => {
  const temps: RegExpMatchArray | null = input.match(regexes.tafTempRegex);

  if (temps !== null) {
    let tempArray: Array<ITemp> = [];
    for (let temp of temps) {
      let tempObj: ITemp = {} as ITemp;

      // Determine whether this is a min or max temperature based on first two letters code
      if (temp.trim().substring(0, 2) === "TX") {
        tempObj.minMax = EMinMax.MAX;
      } else if (temp.trim().substring(0, 2) === "TN") {
        tempObj.minMax = EMinMax.MIN;
      }

      // Get the temperature number which is 2 digits after the 2 letter code
      const temperature = parseInt(temp.trim().substring(2, 4));
      if (!isNaN(temperature)) {
        tempObj.temp = temperature;
      } else {
        // If this fails, something is wrong and this iteration shall be ignored
        continue;
      }

      // Get the expected time of this temperature. This is the last 4 digits of the string
      const time = temp.substring(temp.length-4);
      if (!isNaN(parseInt(time))) {
        tempObj.hour = parseInt(time.substring(0, 2));
        tempObj.minute = parseInt(time.substring(2, 4));
      }

      tempArray.push(tempObj);
    }

    return tempArray;
  } else {
    return undefined;
  }
};

/**
 * Parse taf groups. Taf reports are divided into groups based on time.
 * Parse these groups and elements in them.
 */
export const tafGroupsParser = (
  input: string,
  validity: IValidity
): Array<ITafForecast> | null => {
  const tafGroups: RegExpMatchArray | null = input.match(regexes.tafGroupRegex);

  if (tafGroups !== null) {
    const forecasts: Array<ITafForecast> = tafGroups.map((data) => {
      const forecast: ITafForecast = {} as ITafForecast;
      forecast.validity = {};

      // Get the time in which this group is valid
      const groupDateTime: RegExpMatchArray | null = data.match(
        regexes.tafGroupDateRegex
      );
      if (groupDateTime !== null) {
        if (groupDateTime[2]) {
          // FMxxxx or FMxxxxxxxx
          const day = parseInt(groupDateTime[2].substring(0, 2));
          const hour = parseInt(groupDateTime[2].substring(2, 4));
          forecast.validity.start = generateUTCDate(0, hour, day).toDate();
          // Minute is not parsed (would be in .substring(4,10))
        } else if (groupDateTime[1]) {
          const dayStart = parseInt(groupDateTime[1].substring(0, 2));
          const hourStart = parseInt(groupDateTime[1].substring(2, 4));
          const hourEnd = parseInt(groupDateTime[1].substring(7, 9));
          // If the hour is 24, then it is the next day. Moment will parse hour 24 as 0 the same day
          const dayEnd = parseInt(groupDateTime[1].substring(5, 7)) + (hourEnd === 24 ? 1 : 0);
          forecast.validity.start = generateUTCDate(
            0,
            hourStart,
            dayStart
          ).toDate();
          forecast.validity.end = generateUTCDate(0, hourEnd, dayEnd).toDate();
        }
      }

      // Get all the common forecast properties from the parser and assign the returned object to this forecast
      const commonForecast: ICommonForecastProperties =
        commonForecastParser(data);
      if (commonForecast && commonForecast !== null) {
        Object.assign(forecast, commonForecast);
      }

      // Get if any probability provided
      const prob: string | undefined = probParser(data);
      if (prob !== undefined) {
        if (prob === "30") {
          forecast.prob = EProb.PROB30;
        } else if (prob === "40") {
          forecast.prob = EProb.PROB40;
        }
      }

      // Get min/max temperatures if provided
      const temp: Array<ITemp> | undefined = tafTempParser(data);
      if (temp !== undefined) {
        forecast.temp = temp;
      }

      return forecast;
    });

    // Run through the forecasts and set validity for them
    return forecasts.map((data, i) => {
      if (!data.validity) data.validity = {};
      const nextFM = getNextFm(forecasts, i);

      if (validity && i === 0) {
        // First element ends when the next FM starts
        data.validity.start = validity.start;
        if (nextFM) data.validity.end = nextFM.validity.start; // The first forecast lasts until the next non-temporary period
      } else if (i === forecasts.length - 1) {
        // The last element has end validity like the taf itself
        if (validity) data.validity.end = validity.end;
      } else if (data.changeIndicator?.indexOf("FM") > -1) {
        // FM periods ends when the next start
        if (nextFM) data.validity.end = nextFM.validity.start;
      }
      return data;
    });

    //return forecasts;
  } else {
    return null;
  }
};

/**
 * Get the next forecast period with the change indicator of "FM"
 * @param arr The array of forecasts
 * @param start Optional, an index to start with
 * @returns ITafForecast or undefined if none if found
 */
const getNextFm = (
  arr: Array<ITafForecast>,
  start: number = 0
): ITafForecast | undefined => {
  let nextFM;
  arr.slice(start).map((val) => {
    if (val.changeIndicator?.indexOf("FM") > -1) nextFM = val;
  });
  return nextFM;
};

/**
 * Parse common forecast properties which is present in almost any forecast
 */
export const commonForecastParser = (
  input: string
): ICommonForecastProperties => {
  let weatherObj: ICommonForecastProperties = {};

  // Get if wind is specified (almost always), consists of wind speed and direction
  const wind = windParser(input);
  if (wind !== null) {
    weatherObj.wind = wind;
  }

  // Search for CAVOK in the string
  if (input.toUpperCase().includes(" CAVOK")) {
    weatherObj.cavok = true;
    weatherObj.visibility = "9999";
  } else {
    // If CAVOK is reported, then these will be irrelevant, if not, search for more info in the string

    // Get visibility which consists of visibility and if NDV is present (which is lack of instruments to measure all distances)
    const visibility = visibilityParser(input);
    if (visibility !== null) {
      weatherObj.visibility = visibility.visibility;
      weatherObj.ndv = visibility.ndv;
    }

    // Get if any change indicator (TEMPO, BECMG)
    const changeIndicator: string | undefined = changeParser(input);
    if (changeIndicator !== undefined) {
      weatherObj.changeIndicator = changeIndicator;
    }

    const vv: number | ENA | null = verticalVisibilityParser(input);
    if (vv !== null) {
      weatherObj.verticalVisibility = vv;
    }

    // Check if NSC is set, which is "No significant clouds"
    if (input.toUpperCase().includes(" NSC")) {
      weatherObj.nsc = true;
    } else {
      // Get clouds which consist of the cloud formation and an altitude
      const clouds: Array<ICloud> | null = cloudParser(input);
      if (clouds !== null) {
        weatherObj.clouds = clouds;
      }
    }

    // Check if NSW is set, which is "No significant weather"
    if (input.toUpperCase().includes(" NSW")) {
      weatherObj.nsw = true;
    } else {
      // Get the weather phenomenons
      const weather: Array<IWeatherDesc> | null = weatherParser(input);
      if (weather !== null) {
        weatherObj.weather = weather;
      }
    }
  }

  return weatherObj;
};
